import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { baseURL } from '@/config/APIConfig';


const API = baseURL.dashboardApi;
const VoiceAssistant = ({ onTranscriptionComplete, onNavigate, inline = false, className = "" }) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [error, setError] = useState('');

    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioRef = useRef(null);

    const BACKEND_URL = API || '';

    const startRecording = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const mediaRecorder = new MediaRecorder(stream, {
                mimeType: 'audio/webm;codecs=opus'
            });

            mediaRecorderRef.current = mediaRecorder;
            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                await transcribeAudio(audioBlob);

                // Stop all tracks
                stream.getTracks().forEach(track => track.stop());
            };

            mediaRecorder.start();
            setIsRecording(true);
            setError('');
        } catch (err) {
            console.error('Error accessing microphone:', err);
            setError('Microphone access denied');
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
    };

    const transcribeAudio = async (audioBlob) => {
        setIsProcessing(true);
        try {
            const formData = new FormData();
            formData.append('file', audioBlob, 'audio.webm');

            const response = await fetch(`${BACKEND_URL}/api/voice/transcribe`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Failed to transcribe audio');
            }

            const data = await response.json();
            setTranscript(data.text);

            // Send transcribed text to chat
            if (onTranscriptionComplete) {
                onTranscriptionComplete(data.text);
            }

            // Get AI response and speak it
            await getChatResponse(data.text);
        } catch (err) {
            console.error('Error transcribing audio:', err);
            setError('Failed to transcribe audio');
        } finally {
            setIsProcessing(false);
        }
    };

    const getChatResponse = async (message) => {
        try {
            const response = await fetch(`${BACKEND_URL}/api/voice/chat`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ message }),
            });

            if (!response.ok) {
                throw new Error('Failed to get chat response');
            }

            const data = await response.json();

            // Check if it's a form fill command (for rate search)
            if (data.action === 'form_fill' && data.form_data) {
                // Speak the response
                await speakText(data.response);

                // Navigate to the page if needed and dispatch form fill event
                if (data.page && onNavigate) {
                    // Navigate first
                    onNavigate(data.page);

                    // Wait for navigation and page load, then dispatch form fill event
                    setTimeout(() => {
                        const formFillEvent = new CustomEvent('voiceFormFill', {
                            detail: {
                                origin: data.form_data.origin,
                                destination: data.form_data.destination,
                                auto_search: data.form_data.auto_search
                            }
                        });
                        window.dispatchEvent(formFillEvent);
                        console.log('Voice form fill event dispatched:', data.form_data);
                    }, 1500);
                } else {
                    // Already on the page, dispatch immediately
                    const formFillEvent = new CustomEvent('voiceFormFill', {
                        detail: {
                            origin: data.form_data.origin,
                            destination: data.form_data.destination,
                            auto_search: data.form_data.auto_search
                        }
                    });
                    window.dispatchEvent(formFillEvent);
                    console.log('Voice form fill event dispatched:', data.form_data);
                }
            }
            // Check if it's a navigation command
            else if (data.action === 'navigate' && data.page && onNavigate) {
                // Speak the response
                await speakText(data.response);
                // Navigate after a short delay to let the user hear the response
                setTimeout(() => {
                    onNavigate(data.page);
                }, 1000);
            } else {
                // Regular chat response
                await speakText(data.response);
            }
        } catch (err) {
            console.error('Error getting chat response:', err);
        }
    };

    const speakText = async (text) => {
        setIsSpeaking(true);
        try {
            const response = await fetch(`${BACKEND_URL}/api/voice/synthesize`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    text,
                    voice: 'alloy' // You can make this configurable
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to synthesize speech');
            }

            const audioBlob = await response.blob();
            const audioUrl = URL.createObjectURL(audioBlob);

            if (audioRef.current) {
                audioRef.current.src = audioUrl;
                audioRef.current.play();
            }
        } catch (err) {
            console.error('Error speaking text:', err);
            setIsSpeaking(false);
        }
    };

    const stopSpeaking = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsSpeaking(false);
        }
    };

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.onended = () => {
                setIsSpeaking(false);
            };
        }
    }, []);

    const toggleRecording = () => {
        if (isRecording) {
            stopRecording();
        } else {
            startRecording();
        }
    };

    if (inline) {
        // Inline version for chat box
        return (
            <div className={`flex items-center space-x-2 ${className}`}>
                <button
                    onClick={toggleRecording}
                    disabled={isProcessing}
                    className={`p-2 rounded-lg transition-all ${isRecording
                        ? 'bg-red-500 text-white animate-pulse'
                        : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700'
                        } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                    title={isRecording ? 'Stop Recording' : 'Start Voice Input'}
                >
                    {isProcessing ? (
                        <Loader2 className="w-5 h-5 animate-spin" />
                    ) : isRecording ? (
                        <MicOff className="w-5 h-5" />
                    ) : (
                        <Mic className="w-5 h-5" />
                    )}
                </button>

                {isSpeaking && (
                    <button
                        onClick={stopSpeaking}
                        className="p-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-all animate-pulse"
                        title="Stop Speaking"
                    >
                        <VolumeX className="w-5 h-5" />
                    </button>
                )}

                <audio ref={audioRef} style={{ display: 'none' }} />

                {error && (
                    <span className="text-xs text-red-500">{error}</span>
                )}
            </div>
        );
    }

    // Floating button version
    return (
        <>
            <motion.button
                onClick={toggleRecording}
                disabled={isProcessing}
                className={`fixed bottom-24 right-6 p-4 rounded-full shadow-lg transition-all z-50 ${isRecording
                    ? 'bg-red-500 text-white animate-pulse'
                    : 'bg-[#0072FF] text-white hover:bg-[#0062DD]'
                    } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title={isRecording ? 'Stop Recording' : 'Voice Assistant'}
            >
                {isProcessing ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                ) : isRecording ? (
                    <MicOff className="w-6 h-6" />
                ) : (
                    <Mic className="w-6 h-6" />
                )}
            </motion.button>

            {/* Speaking Indicator */}
            <AnimatePresence>
                {isSpeaking && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed bottom-40 right-6 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg flex items-center space-x-2 z-50"
                    >
                        <Volume2 className="w-5 h-5 animate-pulse" />
                        <span className="text-sm">AI Speaking...</span>
                        <button
                            onClick={stopSpeaking}
                            className="ml-2 p-1 hover:bg-blue-600 rounded transition-all"
                        >
                            <VolumeX className="w-4 h-4" />
                        </button>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Transcript Display */}
            <AnimatePresence>
                {transcript && !isProcessing && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed bottom-40 right-6 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-3 rounded-lg shadow-lg max-w-xs z-50"
                    >
                        <p className="text-sm font-medium mb-1">You said:</p>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{transcript}</p>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Error Display */}
            <AnimatePresence>
                {error && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed bottom-40 right-6 bg-red-500 text-white px-4 py-3 rounded-lg shadow-lg max-w-xs z-50"
                    >
                        <p className="text-sm">{error}</p>
                    </motion.div>
                )}
            </AnimatePresence>

            <audio ref={audioRef} style={{ display: 'none' }} />
        </>
    );
};

export default VoiceAssistant;
